import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Badge, IconButton, Tooltip } from '@mui/material';
import { keyframes } from '@emotion/react';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import { useContadorNotificacoes } from '../../hooks/useNotificacoes';
import NotificationDrawer from './NotificationDrawer';

const bellPulse = keyframes`
  0% { transform: scale(1); }
  25% { transform: scale(1.18); }
  50% { transform: scale(1); }
  75% { transform: scale(1.12); }
  100% { transform: scale(1); }
`;

/**
 * Sino com badge de não lidas + gaveta.
 *
 * @param {{color?: string, iconSize?: 'small'|'medium'|'inherit', centralPath?: string}} props
 */
const NotificationBell = ({ color = 'inherit', iconSize = 'small', centralPath }) => {
  const [open, setOpen] = useState(false);
  const [pulse, setPulse] = useState(false);
  const { naoLidas, total, lidas } = useContadorNotificacoes();
  const previousRef = useRef(naoLidas);

  useEffect(() => {
    const previous = previousRef.current;
    previousRef.current = naoLidas;

    if (naoLidas <= previous) return undefined;

    setPulse(true);
    const timer = setTimeout(() => setPulse(false), 1000);

    return () => clearTimeout(timer);
  }, [naoLidas]);

  const ariaLabel = naoLidas > 0 ? `Notificações (${naoLidas} não lidas)` : 'Notificações';
  const resumoLabel =
    Number.isFinite(total) && total > 0
      ? `${naoLidas} não lida(s) de ${total} · ${lidas ?? 0} lida(s)`
      : 'Notificações';

  // Idempotente de propósito: se a gaveta já está aberta, o toque não faz nada.
  // Sem debounce — descartar o primeiro toque era exatamente o bug relatado
  // ("precisa de ~3 toques para abrir de novo").
  const abrirGaveta = useCallback(() => {
    setOpen((aberta) => (aberta ? aberta : true));
  }, []);

  return (
    <>
      <Tooltip title={resumoLabel} arrow>
        <IconButton
          aria-label={ariaLabel}
          onClick={abrirGaveta}
          sx={{
            color,
            ...(pulse ? { animation: `${bellPulse} 0.9s ease` } : {}),
          }}
        >
          <Badge
            badgeContent={naoLidas}
            color="error"
            max={99}
            overlap="circular"
            invisible={!naoLidas}
          >
            <NotificationsNoneIcon fontSize={iconSize} />
          </Badge>
        </IconButton>
      </Tooltip>

      <NotificationDrawer open={open} onClose={() => setOpen(false)} centralPath={centralPath} />
    </>
  );
};

export default NotificationBell;
